import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Sparkles, Send, RefreshCw, History, Copy, Check, AlertCircle, ChevronDown, Plus, Shield, Loader2, Wand2, Video, ShoppingBag, SlidersHorizontal, X, Download, Mic, MicOff } from 'lucide-react';
import authService from '../services/AuthService';
import { API_BASE } from '../consts';
import {
    InfluencerResults, rangesFromFilters, hasRanges, needsFullData, passesRanges, deriveInfluencer,
    fetchCardData, loadQuick, mergeCard, sleep, CARD_BATCH, CARD_BATCH_PAUSE_MS, PAGE_SIZE, Pager,
} from '../components/MipGptInfluencer';


const INF_EXAMPLES = [
    'Kitchen gadgets under $40 with 3 or fewer videos on the listing',
    'Beauty products selling 500+ a month',
    'Pet supplies $20-$60 with 300+ monthly sales',
    'Home & Kitchen products with no videos on the listing',
];

const EXAMPLES = [
    'Home & Kitchen products with 30+ rank drops a month',
    'Home & Kitchen items over $20 that Amazon isn\'t selling, 4 sellers max',
    'Best-selling sports products under 1 lb with at least 200 sold monthly',
    'Toys $15-$40 with no variations and 3+ sellers',
    'Walmart: kitchen products under $25 with 300+ monthly sales',
];

const GATING_OPTIONS = [
    { key: 'all', label: 'All results', desc: 'Every match. We check your gating after.' },
    { key: 'ungated', label: 'Only ungated', desc: 'Checked before we pull data. Recommended.' },
    { key: 'ungated_approval', label: 'Ungated + Approval', desc: 'Also products you can apply for.' },
];
const INF_GATING_OPTIONS = [
    { key: 'all', label: 'All results', desc: 'Every match, with campaigns or onsite commission.' },
    { key: 'cc_only', label: 'Only with Creator Connections', desc: 'Campaigns checked first. Recommended.' },
];
const GATING_CHIP = { all: 'All results', ungated: '🛡 Ungated only', ungated_approval: '🛡 Ungated + Approval', cc_only: '🤝 Only with Creator Connections' };

const SORT_LABELS = {
    sales_rank_asc: 'Best rank first',
    monthly_sold_desc: 'Most sold first',
    rank_drops_desc: 'Most rank drops first',
    price_asc: 'Cheapest first',
    price_desc: 'Most expensive first',
    price_drop_desc: 'Biggest price drop first',
    reviews_desc: 'Most reviews first',
};

const GATED_STYLE = {
    ungated: { label: '✓ Ungated', cls: 'bg-green-50 border-green-400 text-green-800' },
    approval: { label: '⚠ Approval', cls: 'bg-orange-50 border-orange-400 text-orange-800' },
    gated: { label: '✗ Gated', cls: 'bg-red-50 border-red-400 text-red-800' },
    needs_login: { label: 'Link Amazon', cls: 'bg-yellow-50 border-yellow-300 text-yellow-800' },
    error: { label: 'Retry', cls: 'bg-gray-50 border-gray-300 text-gray-600' },
};

const PRICE_GAP_THRESHOLD = 0.25;   // highlight Walmart matches 25%+ cheaper / more expensive than Amazon
const WM_BATCH = 6;                 // Walmart mode: UPC lookups at a time
const wmPause = () => 3000 + Math.random() * 2000;   // Walmart mode: wait 3-5s between batches
const ROUND_TARGET = 100;           // ungated / influencer rounds: stop once this many products are accepted

const LWA_URL = () => `https://sellercentral.amazon${authService.getAmazonMarketplace()}/apps/authorize/consent?application_id=amzn1.sp.solution.18aecca2-d85f-492c-b0ba-d121066f7ef8&state=${encodeURIComponent(authService.credentials?.email || '')}`;

const WmLogo = ({ className = 'w-3.5 h-3.5' }) => {
    const [failed, setFailed] = useState(false);
    if (failed) return <span className="font-black">WM</span>;
    return <img src="/icons/walmart.png" alt="Walmart" className={`${className} object-contain`} onError={() => setFailed(true)} />;
};

const legacyChips = (f = {}, cur) => {
    const chips = [];
    const add = (v, label) => v !== undefined && v !== null && v !== '' && chips.push(label);
    if (f.gating) chips.push(GATING_CHIP[f.gating] || f.gating);
    else if (f.ungated_only) chips.push(GATING_CHIP.ungated);
    if (f.categories?.length) chips.push(f.categories.join(', '));
    add(f.title_keywords, `Title: "${f.title_keywords}"`);
    if (f.brands?.length) chips.push(`Brand: ${f.brands.join(', ')}`);
    add(f.min_price, `Price ≥ ${cur}${f.min_price}`);
    add(f.max_price, `Price ≤ ${cur}${f.max_price}`);
    add(f.min_monthly_sold, `${Number(f.min_monthly_sold).toLocaleString()}+ sold/mo`);
    add(f.max_sales_rank, `Rank ≤ ${Number(f.max_sales_rank).toLocaleString()}`);
    add(f.min_sales_rank, `Rank ≥ ${Number(f.min_sales_rank).toLocaleString()}`);
    add(f.max_sellers, `≤ ${f.max_sellers} sellers`);
    add(f.min_sellers, `≥ ${f.min_sellers} sellers`);
    if (f.no_amazon_offer) chips.push('No Amazon offer');
    add(f.min_amazon_oos_percent, `Amazon OOS ≥ ${f.min_amazon_oos_percent}%`);
    add(f.min_reviews, `≥ ${f.min_reviews} reviews`);
    add(f.max_reviews, `≤ ${f.max_reviews} reviews`);
    add(f.min_rating, `★ ${f.min_rating}+`);
    add(f.min_price_drop_percent_90, `Price drop ≥ ${f.min_price_drop_percent_90}% (90d)`);
    add(f.min_rank_drops_30, `${f.min_rank_drops_30}+ rank drops (30d)`);
    add(f.min_rank_drops_90, `${f.min_rank_drops_90}+ rank drops (90d)`);
    add(f.max_variations, `≤ ${f.max_variations} variations`);
    add(f.max_weight_lbs, `≤ ${f.max_weight_lbs} lbs`);
    add(f.max_listing_videos, `≤ ${f.max_listing_videos} listing videos`);
    add(f.max_total_videos, `≤ ${f.max_total_videos} total videos`);
    add(f.max_upper_videos, `≤ ${f.max_upper_videos} upper videos`);
    add(f.max_bottom_videos, `≤ ${f.max_bottom_videos} bottom videos`);
    add(f.max_influencer_videos, `≤ ${f.max_influencer_videos} influencer videos`);
    add(f.min_listing_videos, `≥ ${f.min_listing_videos} listing videos`);
    if (f.lowest_price_90_days) chips.push('Lowest price in 90 days');
    if (f.sort) chips.push(SORT_LABELS[f.sort] || f.sort);
    return chips;
};

// Chips under the answer: what was applied, how vague words were read, and what couldn't be applied
const filterChips = (f = {}, cur) => {
    const gate = [
        ...(f.marketplace === 'walmart' ? [{ text: '🛒 Walmart · matched by UPC', kind: 'applied' }] : []),
        ...(f.gating && f.gating !== 'all' ? [{ text: GATING_CHIP[f.gating] || f.gating, kind: 'applied' }] : []),
    ];
    if (!f.chips) return [...gate, ...legacyChips({ ...f, gating: undefined }, cur).map(text => ({ text, kind: 'applied' }))];
    return [
        ...gate,
        ...(f.chips.applied || []).map(text => ({ text, kind: 'applied' })),
        ...(f.chips.unsupported || []).map(text => ({ text, kind: 'unsupported' })),
    ];
};
// "How we read it" — short notes for vague words, one quiet line
const readNotes = (f = {}) => (f.chips?.interpreted || []).filter(Boolean).slice(0, 3);
const CHIP_CLS = {
    applied: 'bg-blue-50 text-blue-800 border-blue-200',
    unsupported: 'bg-amber-50 text-amber-800 border-amber-300',
};
const CHIP_PREFIX = { applied: '', unsupported: "Couldn't apply: " };

const detectGating = (text, tool) => {
    const t = text.toLowerCase();
    if (tool === 'influencer') return /creator connection|\bcc\b|campaign/.test(t) ? 'cc_only' : null;
    if (/approv/.test(t) && /ungated|gated/.test(t)) return 'ungated_approval';
    if (/ungated|not gated|i can sell|allowed to sell/.test(t)) return 'ungated';
    return null;
};

const detectMarketplace = (text) => {
    const t = text.toLowerCase();
    if (/walmart/.test(t)) return 'walmart';
    if (/amazon|\bfba\b|\bfbm\b/.test(t)) return 'amazon';
    return null;
};

const api = async (path, opts = {}) => {
    const res = await authService.authenticatedFetch(API_BASE + path, opts);
    let data = {};
    try { data = await res.json(); } catch { /* empty */ }
    return { ok: res.ok, status: res.status, data };
};

const postJson = (path, body) => api(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
});

const walmartSearch = async (term, type) => {
    try {
        const { ok, data } = await postJson('/api/walmart/search', { search_term: term, search_type: type });
        return ok && Array.isArray(data) ? data : [];
    } catch { return []; }
};

const CopyBtn = ({ text }) => {
    const [done, setDone] = useState(false);
    return (
        <button
            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(text || ''); setDone(true); setTimeout(() => setDone(false), 1200); }}
            className="text-gray-400 hover:text-gray-700"
            title="Copy"
        >
            {done ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
        </button>
    );
};

const Stat = ({ label, value, sub, className = 'bg-gray-50 border-gray-200 text-gray-800', onClick, title }) => (
    <div onClick={onClick} title={title} className={`rounded p-1.5 border text-center ${onClick ? 'cursor-pointer hover:brightness-95' : ''} ${className}`}>
        <div className="text-gray-500 text-[10px] font-semibold whitespace-nowrap">{label}</div>
        <div className="font-bold text-sm leading-tight">{value ?? '—'}</div>
        {sub && <div className="text-[9px] text-gray-400 whitespace-nowrap">{sub}</div>}
    </div>
);

const Info = ({ label, children }) => (
    <span className="whitespace-nowrap"><span className="text-gray-400">{label}:</span> <span className="font-semibold text-gray-700">{children}</span></span>
);

// ── Walmart research panel (opens under a card) — info only, no profit / ROI ──
const WalmartPanel = ({ p, wm, currency }) => {
    if (wm.loading) {
        return (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
                <Loader2 size={14} className="animate-spin text-blue-600" /> Searching Walmart…
            </div>
        );
    }
    if (!wm.items.length) {
        return <div className="text-xs text-gray-500 py-3">No Walmart results found for this product.</div>;
    }
    return (
        <div className="space-y-1.5 pt-2">
            <div className="text-[11px] font-semibold text-gray-500 flex items-center gap-1">
                <WmLogo /> Walmart results · {wm.mode === 'upc' ? 'matched by UPC' : '⚠️ matched by name - make sure it\'s the same product (size / pack count)'}
            </div>
            {wm.items.map(w => {
                const wmPrice = parseFloat(w.price_value ?? w.price);
                const hasPrice = !isNaN(wmPrice) && wmPrice > 0;
                const gap = hasPrice && p.price ? (wmPrice - p.price) / p.price : null;
                const bigGap = gap != null && Math.abs(gap) >= PRICE_GAP_THRESHOLD;
                const ship = parseFloat(w.shipping_price);
                const rating = parseFloat(w.average_rating ?? w.rating);
                const reviews = w.number_of_reviews ?? w.review_count;
                const seller = w.seller_name || w.sellerName;
                const gtin = w.gtin || w.upc;
                const variations = w.total_variations ?? (Array.isArray(w.variations) ? w.variations.length : null);
                const dims = w.dimensions;
                const badges = (w.badges || []).map(b => b?.text).filter(Boolean);
                return (
                    <div key={w.item_id} className="flex items-start gap-3 rounded-lg px-2 py-2 border bg-blue-50/40 border-blue-100">
                        <a href={`https://www.walmart.com/ip/${w.item_id}`} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 relative group">
                            <img src={w.image || w.image_url || '/noimage.png'} alt="" className="w-12 h-12 object-contain bg-white rounded border-2 border-gray-200 transition-all duration-300 group-hover:scale-[3] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-blue-400 relative" onError={(e) => { e.target.src = '/noimage.png'; }} />
                        </a>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-2">
                                <a href={`https://www.walmart.com/ip/${w.item_id}`} target="_blank" rel="noopener noreferrer" className="flex-1 text-xs font-semibold text-gray-800 hover:text-blue-700 line-clamp-1" title={w.name}>
                                    {w.name}
                                </a>
                                <span className="text-sm font-bold text-blue-800 flex-shrink-0">{hasPrice ? `$${wmPrice.toFixed(2)}` : '—'}</span>
                            </div>
                            {bigGap && (
                                <div
                                    className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded cursor-help"
                                    title="Just for your info: a price difference doesn't mean you can't find a lead somewhere else."
                                >
                                    💡 Price difference: Walmart is {Math.round(Math.abs(gap) * 100)}% {gap < 0 ? 'cheaper' : 'more expensive'} than Amazon ({currency}{p.price.toFixed(2)})
                                </div>
                            )}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] mt-0.5">
                                {w.brand && <Info label="Brand">{w.brand}</Info>}
                                <Info label="Seller">{seller || 'Unknown'}</Info>
                                {w.is_walmart && <span className="text-blue-700 font-bold">Sold by Walmart</span>}
                                <Info label="Offers">{w.offers || 1}</Info>
                                <span className={`font-bold ${w.in_stock === false ? 'text-red-600' : 'text-green-700'}`}>{w.in_stock === false ? 'Out of stock' : 'In stock'}</span>
                                {!isNaN(ship) && <Info label="Shipping">{ship > 0 ? `$${ship.toFixed(2)}` : 'Free'}</Info>}
                                {reviews > 0 && <Info label="Reviews">{Number(reviews).toLocaleString()}{!isNaN(rating) && rating > 0 ? ` (★${rating.toFixed(1)})` : ''}</Info>}
                                {w.category && <Info label="Category">{w.category}{w.sub_category ? ` › ${w.sub_category}` : ''}</Info>}
                                {variations > 0 && <Info label="Variations">{variations}</Info>}
                                {w.weight && <Info label="Weight">{w.weight} lb</Info>}
                                {dims?.length && dims?.width && dims?.height && <Info label="Dims">{dims.length}" × {dims.width}" × {dims.height}"</Info>}
                                <span className="flex items-center gap-1"><Info label="Item ID">{w.item_id}</Info><CopyBtn text={String(w.item_id)} /></span>
                                {gtin && <span className="flex items-center gap-1"><Info label="GTIN">{gtin}</Info><CopyBtn text={String(gtin)} /></span>}
                            </div>
                            {badges.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {badges.map(b => <span key={b} className="text-[9px] font-bold bg-yellow-100 text-yellow-800 border border-yellow-300 px-1.5 py-0.5 rounded">{b}</span>)}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ── Product card ─────────────────────────────────────────────────────────────
// Keepa price-history mini chart (same image as the DealCard); hover shows it bigger, click opens Keepa
const KEEPA_GRAPH_DOMAIN = { '.com': 'com', '.co.uk': 'co.uk', '.ca': 'ca', '.com.mx': 'com.mx' };
const KeepaMini = ({ asin, amazonDomain, keepaDomain }) => {
    const src = `https://wsrv.nl/?url=${encodeURIComponent(`https://graph.keepa.com/pricehistory.png?asin=${asin}&domain=${KEEPA_GRAPH_DOMAIN[amazonDomain] || 'com'}`)}`;
    return (
        <div
            className="relative group/keepa rounded border border-gray-200 bg-gray-50 overflow-visible cursor-pointer hover:border-teal-400 transition-colors h-full min-h-[48px]"
            onClick={() => window.open(`https://keepa.com/#!product/${keepaDomain}-${asin}`, '_blank')}
            title="Open on Keepa"
        >
            <img src={src} alt="Keepa" loading="lazy" className="w-full h-full object-contain rounded"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
            <div className="w-full h-full justify-center items-center gap-1 text-gray-500 text-xs" style={{ display: 'none' }}>
                <img src="/icons/keepa.png" className="w-4 h-4" alt="" /> <span>Keepa</span>
            </div>
            <div className="absolute right-0 bottom-full mb-2 hidden group-hover/keepa:block z-[60]" style={{ minWidth: 'min(500px, 90vw)' }}>
                <div className="bg-white border-2 border-teal-300 rounded-lg shadow-2xl p-3">
                    <div className="text-xs font-semibold text-gray-700 mb-2 border-b pb-1">Keepa Price History</div>
                    <img src={src} alt="Keepa Chart" style={{ width: '100%', height: 'auto', borderRadius: '4px' }} />
                </div>
            </div>
        </div>
    );
};

const ResultCard = ({ p, index, amazonDomain, currency, keepaDomain, gated, onRecheck }) => {
    const [wmOpen, setWmOpen] = useState(false);
    const [wm, setWm] = useState(null);
    const amazonUrl = `https://www.amazon${amazonDomain}/dp/${p.asin}`;
    const g = GATED_STYLE[gated];

    // Manual check: UPC and title searched at the same time — UPC results win, title results otherwise
    const loadWalmart = async () => {
        setWm({ loading: true, items: [] });
        const [byUpc, byName] = await Promise.all([
            /^\d{8,14}$/.test(p.upc || '') ? walmartSearch(p.upc, 'upc') : Promise.resolve([]),
            walmartSearch((p.title || '').slice(0, 100), 'name'),
        ]);
        const items = byUpc.length ? byUpc : byName;
        setWm({ loading: false, items: items.slice(0, 5), mode: byUpc.length ? 'upc' : 'name' });
    };

    const toggleWalmart = () => {
        if (wmOpen) { setWmOpen(false); return; }
        setWmOpen(true);
        if (!wm) loadWalmart();
    };

    const onGatedClick = () => {
        if (gated === 'ungated') window.open(`https://sellercentral.amazon${amazonDomain}/product-search/search?q=${p.asin}`, '_blank');
        else if (gated === 'gated' || gated === 'approval') window.open(`https://sellercentral.amazon${amazonDomain}/hz/approvalrequest/restrictions/approve?asin=${p.asin}`, '_blank');
        else if (gated === 'needs_login') window.open(LWA_URL(), '_blank');
        else if (gated === 'error') onRecheck(p.asin);
    };

    const wmBtnCls = wmOpen ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100';

    return (
        <div className="border border-gray-200 rounded-lg bg-white p-3 hover:shadow-md transition">
            <div className="flex gap-3 items-start">
                <div className="text-xs font-bold text-gray-300 w-6 pt-1 text-right flex-shrink-0">{index + 1}</div>
                <div className="flex flex-col items-center gap-1 flex-shrink-0 w-[76px]">
                    <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="relative group">
                        <img
                            src={p.image || '/noimage.png'}
                            alt=""
                            className="w-16 h-16 object-contain rounded border-2 border-gray-200 bg-white transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-green-400 relative"
                            onError={(e) => { e.target.src = '/noimage.png'; }}
                        />
                    </a>
                    <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] font-semibold text-gray-600 hover:text-gray-900" title="Open on Amazon">
                        <img src="/icons/amazon.png" alt="" className="w-4 h-4 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                        Amazon
                    </a>
                    <button
                        onClick={toggleWalmart}
                        className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border transition whitespace-nowrap ${wmBtnCls}`}
                        title="Look for this product on Walmart"
                    >
                        Check <WmLogo className="w-3.5 h-3.5" />
                    </button>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                        <a href={`https://www.google.com/search?q=${encodeURIComponent(p.title || '')}&gl=us&hl=en`} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 mt-0.5" title="Search this title on Google">
                            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4 object-contain hover:scale-110 transition" />
                        </a>
                        <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="flex-1 font-semibold text-sm text-gray-800 hover:text-teal-700 line-clamp-1" title={p.title}>
                            {p.title}
                        </a>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-0.5">
                        {p.brand && <span className="font-semibold text-gray-600">{p.brand}</span>}
                        <span className="flex items-center gap-1">ASIN: {p.asin} <CopyBtn text={p.asin} /></span>
                        {p.upc && <span className="flex items-center gap-1">UPC: {p.upc} <CopyBtn text={p.upc} /></span>}
                        {p.category && <span>{p.category}</span>}
                        <a href={`https://keepa.com/#!product/${keepaDomain}-${p.asin}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline font-semibold">Keepa ↗</a>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-8 gap-1.5 mt-2">
                        <Stat
                            label="Gated"
                            value={g ? g.label : <Loader2 size={14} className="animate-spin mx-auto text-blue-500" />}
                            className={g ? g.cls : 'bg-blue-50 border-blue-200 text-blue-700'}
                            onClick={g ? onGatedClick : undefined}
                            title={gated === 'ungated' ? 'Open in Seller Central' : gated === 'gated' || gated === 'approval' ? 'Apply for approval in Seller Central' : gated === 'error' ? 'Check again' : 'Checking…'}
                        />
                        <Stat label="Price" value={p.price != null ? `${currency}${p.price.toFixed(2)}` : null}
                            sub={p.price_avg90 ? `90d avg ${currency}${p.price_avg90.toFixed(2)}` : null}
                            className="bg-green-50 border-green-200 text-green-800" />
                        <Stat label="BSR" value={p.sales_rank ? `#${p.sales_rank.toLocaleString()}` : null}
                            sub={p.bsr_avg90 ? `90d avg #${p.bsr_avg90.toLocaleString()}` : null} />
                        <Stat label="Drops 30d" value={p.rank_drops_30} className="bg-purple-50 border-purple-200 text-purple-800" />
                        <Stat label="Sold / mo" value={p.monthly_sold ? `${p.monthly_sold.toLocaleString()}+` : null} className="bg-purple-50 border-purple-200 text-purple-800" />
                        <Stat
                            label="Sellers"
                            value={p.sellers == null ? null : (
                                <span className="inline-flex items-center gap-1">
                                    {p.sellers}
                                    {p.amazon_selling && <img src="/icons/amazon.png" alt="Amazon sells" title="Amazon is selling this product" className="w-4 h-4 object-contain" />}
                                </span>
                            )}
                            title={p.amazon_selling ? 'Amazon is one of the sellers' : undefined} />
                        <Stat label="Variations" value={p.variations || 'None'} className={p.variations > 1 ? 'bg-orange-50 border-orange-200 text-orange-800' : 'bg-gray-50 border-gray-200 text-gray-800'} />
                        <KeepaMini asin={p.asin} amazonDomain={amazonDomain} keepaDomain={keepaDomain} />
                    </div>
                    {wmOpen && wm && <WalmartPanel p={p} wm={wm} currency={currency} />}
                </div>
            </div>
        </div>
    );
};

// ── Walmart mode: skeleton + one row per product matched on Walmart by UPC ──
const WmSkeleton = () => (
    <div className="border border-blue-100 rounded-lg bg-white p-3 flex gap-3 items-start animate-pulse">
        <div className="w-6" />
        <div className="w-16 h-16 rounded bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-2">
            <div className="h-3.5 w-2/3 rounded bg-gray-200" />
            <div className="h-3 w-1/3 rounded bg-gray-100" />
            <div className="grid grid-cols-4 xl:grid-cols-7 gap-1.5">
                {[0, 1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-10 rounded bg-gray-100" />)}
            </div>
        </div>
    </div>
);

const WalmartRow = ({ p, w, index, onAmazonOpen, ...amazon }) => {
    const [open, setOpen] = useState(false);
    const url = `https://www.walmart.com/ip/${w.item_id}`;
    const price = parseFloat(w.price_value ?? w.price);
    const ship = parseFloat(w.shipping_price);
    const rating = parseFloat(w.average_rating ?? w.rating);
    const reviews = w.number_of_reviews ?? w.review_count;
    const seller = w.seller_name || w.sellerName || 'Unknown';
    const gtin = w.gtin || w.upc || p.upc;
    const variations = w.total_variations ?? (Array.isArray(w.variations) ? w.variations.length : null);
    const toggle = () => { if (!open) onAmazonOpen?.(p.asin); setOpen(o => !o); };
    // Big price gap between Amazon and Walmart -> small "potential A2W / W2A" badge (info only)
    const gap = !isNaN(price) && price > 0 && p.price ? (price - p.price) / p.price : null;
    const bigGap = gap != null && Math.abs(gap) >= PRICE_GAP_THRESHOLD;

    return (
        <div className="border border-blue-200 rounded-lg bg-white p-3 hover:shadow-md transition">
            <div className="flex gap-3 items-start">
                <div className="text-xs font-bold text-gray-300 w-6 pt-1 text-right flex-shrink-0">{index + 1}</div>
                <div className="flex flex-col items-center gap-1 flex-shrink-0 w-[76px]">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="relative group">
                        <img src={w.image || w.image_url || p.image || '/noimage.png'} alt=""
                            className="w-16 h-16 object-contain rounded border-2 border-gray-200 bg-white transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-blue-400 relative"
                            onError={(e) => { e.target.src = '/noimage.png'; }} />
                    </a>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] font-semibold text-gray-600 hover:text-gray-900" title="Open on Walmart">
                        <WmLogo className="w-4 h-4" /> Walmart
                    </a>
                    <button onClick={toggle} title="See this product's Amazon data"
                        className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border transition whitespace-nowrap ${open ? 'bg-gray-800 text-white border-gray-800' : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'}`}>
                        Check <img src="/icons/amazon.png" alt="Amazon" className="w-3.5 h-3.5 object-contain" />
                    </button>
                    {bigGap && (
                        <span
                            className="text-[9px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-1 py-0.5 rounded cursor-help whitespace-nowrap"
                            title={`Amazon ${amazon.currency}${p.price.toFixed(2)} vs Walmart $${price.toFixed(2)} (${Math.round(Math.abs(gap) * 100)}% difference). Just for your info: check fees and pack size before buying.`}
                        >
                            💡 Potential {gap > 0 ? 'A2W' : 'W2A'}
                        </span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="block font-semibold text-sm text-gray-800 hover:text-blue-700 line-clamp-1" title={w.name}>
                        {w.name || p.title}
                    </a>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-0.5">
                        {w.brand && <span className="font-semibold text-gray-600">{w.brand}</span>}
                        <span className="flex items-center gap-1">Item ID: {w.item_id} <CopyBtn text={String(w.item_id)} /></span>
                        {gtin && <span className="flex items-center gap-1">UPC: {gtin} <CopyBtn text={String(gtin)} /></span>}
                        {w.category && <span>{w.category}{w.sub_category ? ` › ${w.sub_category}` : ''}</span>}
                        <span className="text-green-700 font-semibold">✓ UPC match</span>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-7 gap-1.5 mt-2">
                        <Stat label="Walmart price" value={!isNaN(price) && price > 0 ? `$${price.toFixed(2)}` : null} className="bg-blue-50 border-blue-200 text-blue-800" />
                        <Stat label="Seller" value={<span className="block truncate max-w-[120px] mx-auto" title={seller}>{w.is_walmart ? 'Walmart' : seller}</span>} />
                        <Stat label="Offers" value={w.offers || 1} />
                        <Stat label="Stock" value={w.in_stock === false ? 'Out' : 'In stock'} className={w.in_stock === false ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-800'} />
                        <Stat label="Shipping" value={isNaN(ship) ? null : ship > 0 ? `$${ship.toFixed(2)}` : 'Free'} />
                        <Stat label="Reviews" value={reviews > 0 ? Number(reviews).toLocaleString() : null} sub={!isNaN(rating) && rating > 0 ? `★${rating.toFixed(1)}` : null} />
                        <Stat label="Variations" value={variations > 0 ? variations : 'None'} />
                    </div>
                </div>
            </div>
            {open && (
                <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
                    <ResultCard p={p} index={index} {...amazon} />
                </div>
            )}
        </div>
    );
};

// Walmart mode: product with no UPC match -> Amazon info + manual Walmart search by title
const NoMatchRow = ({ p, index, amazonDomain, currency }) => {
    const [open, setOpen] = useState(false);
    const [wm, setWm] = useState(null);
    const amazonUrl = `https://www.amazon${amazonDomain}/dp/${p.asin}`;
    const searchByTitle = async () => {
        if (open) { setOpen(false); return; }
        setOpen(true);
        if (wm) return;
        setWm({ loading: true, items: [] });
        const items = await walmartSearch((p.title || '').slice(0, 100), 'name');
        setWm({ loading: false, items: items.slice(0, 5), mode: 'name' });
    };
    return (
        <div className="border border-dashed border-gray-300 rounded-lg bg-white/70 p-3">
            <div className="flex gap-3 items-start">
                <div className="text-xs font-bold text-gray-300 w-6 pt-1 text-right flex-shrink-0">{index + 1}</div>
                <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="relative group flex-shrink-0">
                    <img src={p.image || '/noimage.png'} alt=""
                        className="w-12 h-12 object-contain rounded border-2 border-gray-200 bg-white transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl relative"
                        onError={(e) => { e.target.src = '/noimage.png'; }} />
                </a>
                <div className="flex-1 min-w-0">
                    <a href={amazonUrl} target="_blank" rel="noopener noreferrer" className="block font-semibold text-sm text-gray-700 hover:text-teal-700 line-clamp-1" title={p.title}>
                        {p.title}
                    </a>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-0.5">
                        <span className="flex items-center gap-1">ASIN: {p.asin} <CopyBtn text={p.asin} /></span>
                        {p.upc ? <span className="flex items-center gap-1">UPC: {p.upc} <CopyBtn text={p.upc} /></span> : <span className="text-red-600">No UPC</span>}
                        <Info label="Amazon price">{p.price != null ? `${currency}${p.price.toFixed(2)}` : '—'}</Info>
                        {p.sales_rank && <Info label="BSR">#{p.sales_rank.toLocaleString()}</Info>}
                        {p.monthly_sold && <Info label="Sold/mo">{p.monthly_sold.toLocaleString()}+</Info>}
                    </div>
                    <button
                        onClick={searchByTitle}
                        className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold border transition ${open ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'}`}
                    >
                        <WmLogo /> {open ? 'Hide Walmart results' : 'No UPC match · Click here to search Walmart by title'}
                    </button>
                    {open && wm && <WalmartPanel p={p} wm={wm} currency={currency} />}
                </div>
            </div>
        </div>
    );
};

// ── Voice input: browser speech-to-text (Chrome / Edge / Safari), adds to what's already typed ──
const SpeechRec = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
const MicButton = ({ value, onChange, disabled, stopKey }) => {
    const [on, setOn] = useState(false);
    const recRef = useRef(null);
    const stopRef = useRef(false);                             // true only when the user clicks stop
    useEffect(() => () => { stopRef.current = true; recRef.current?.abort(); }, []);
    useEffect(() => {                                          // Ask was clicked -> stop listening right away
        if (!stopKey) return;
        stopRef.current = true;
        recRef.current?.abort();                               // abort (not stop) so no late words refill the box
        setOn(false);
    }, [stopKey]);
    if (!SpeechRec) return null;   // browser can't do it -> no button
    const toggle = () => {
        if (on) { stopRef.current = true; recRef.current?.stop(); return; }
        stopRef.current = false;
        let text = value.trim() ? `${value.trim()} ` : '';     // everything said so far (kept across restarts)
        const rec = new SpeechRec();
        rec.lang = navigator.language || 'en-US';
        rec.interimResults = true;
        rec.continuous = true;                                 // don't stop after the first pause
        rec.onresult = (e) => {
            let interim = '';
            for (let i = e.resultIndex; i < e.results.length; i++) {
                if (e.results[i].isFinal) text += `${e.results[i][0].transcript.trim()} `;
                else interim += e.results[i][0].transcript;
            }
            onChange((text + interim).slice(0, 500));
        };
        rec.onend = () => {                                    // browser ended it on its own -> keep going
            if (!stopRef.current) { try { rec.start(); return; } catch { /* fall through */ } }
            setOn(false);
        };
        rec.onerror = (e) => {
            if (e.error === 'not-allowed' || e.error === 'service-not-allowed') { stopRef.current = true; setOn(false); }
        };
        recRef.current = rec;
        rec.start();
        setOn(true);
    };
    return (
        <button
            type="button"
            onClick={toggle}
            disabled={disabled}
            title={on ? 'Stop listening' : 'Speak your search'}
            className={`h-12 w-12 flex-shrink-0 flex items-center justify-center rounded-lg border transition disabled:opacity-50 ${on ? 'bg-red-500 border-red-500 text-white animate-pulse' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
        >
            {on ? <MicOff size={18} /> : <Mic size={18} />}
        </button>
    );
};

// ── "Version 1.0 is live!!" badge with a confetti burst (on load and on hover) ──
const CONFETTI_COLORS = ['#14b8a6', '#2563eb', '#a855f7', '#f59e0b', '#ef4444', '#22c55e'];
const LiveBadge = () => {
    const [burst, setBurst] = useState(1);              // changing the key replays the animation
    const pieces = useMemo(() => Array.from({ length: 24 }, (_, i) => ({
        x: Math.round(Math.cos((i / 24) * Math.PI * 2) * (40 + Math.random() * 50)),
        y: Math.round(Math.sin((i / 24) * Math.PI * 2) * (25 + Math.random() * 35) - 20),
        r: Math.round(Math.random() * 720 - 360),
        c: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        d: Math.round(Math.random() * 150),
        round: i % 3 === 0,
    })), []);
    return (
        <span className="relative inline-flex" onMouseEnter={() => setBurst(b => b + 1)}>
            <style>{`
                @keyframes mspConfetti { 0% { transform: translate(0,0) rotate(0); opacity: 1; } 100% { transform: translate(var(--x), calc(var(--y) + 40px)) rotate(var(--r)); opacity: 0; } }
                @keyframes mspLivePulse { 0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,.45); } 50% { box-shadow: 0 0 0 6px rgba(34,197,94,0); } }
            `}</style>
            <span className="text-[11px] font-bold uppercase tracking-wider text-white bg-gradient-to-r from-green-500 via-teal-500 to-blue-600 px-2.5 py-0.5 rounded-full flex items-center gap-1 cursor-default"
                style={{ animation: 'mspLivePulse 2s ease-in-out infinite' }}>
                🎉 Version 1.0 is live!!
            </span>
            <span key={burst} className="pointer-events-none absolute left-1/2 top-1/2">
                {pieces.map((p, i) => (
                    <span key={i} className={`absolute block w-1.5 h-2.5 ${p.round ? 'rounded-full w-2 h-2' : 'rounded-sm'}`}
                        style={{
                            background: p.c, '--x': `${p.x}px`, '--y': `${p.y}px`, '--r': `${p.r}deg`,
                            animation: `mspConfetti 1.3s cubic-bezier(.2,.7,.4,1) ${p.d}ms forwards`, opacity: 0,
                        }} />
                ))}
            </span>
        </span>
    );
};

// ── Gating picker ────────────────────────────────────────────────────────────
const GatingPicker = ({ value, onChange, disabled, options = GATING_OPTIONS }) => (
    <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] font-semibold text-gray-500">Show me:</span>
        {options.map(o => (
            <button
                key={o.key}
                type="button"
                disabled={disabled}
                onClick={() => onChange(o.key)}
                title={o.desc}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition disabled:opacity-50 ${value === o.key
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-teal-400'}`}
            >
                {o.key === 'cc_only' ? '🤝 ' : o.key !== 'all' ? '🛡 ' : ''}{o.label}
            </button>
        ))}
    </div>
);

// ── Seller Pro / Influencer Pro switch ───────────────────────────────────────
const ToolSwitch = ({ value, onChange }) => (
    <div className="inline-flex bg-white rounded-xl shadow p-1 gap-1">
        {[
            ['seller', 'MSP GPT · Seller Pro', ShoppingBag, 'bg-gradient-to-r from-teal-500 to-blue-600'],
            ['influencer', 'MIP GPT · Influencer Pro', Video, 'bg-gradient-to-r from-blue-600 to-purple-600'],
        ].map(([key, label, Icon, activeBg]) => (
            <button
                key={key}
                onClick={() => onChange(key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${value === key ? `${activeBg} text-white shadow` : 'text-gray-500 hover:text-gray-800'}`}
            >
                <Icon size={16} /> {label}
            </button>
        ))}
    </div>
);

// Seller metrics — every one can be filtered with min / max on the page
const SELLER_METRICS = [
    { key: 'price', label: 'Price $', get: p => p.price },
    { key: 'sales_rank', label: 'BSR', get: p => p.sales_rank },
    { key: 'monthly_sold', label: 'Sold / mo', get: p => p.monthly_sold },
    { key: 'rank_drops_30', label: 'Drops 30d', get: p => p.rank_drops_30 },
    { key: 'rank_drops_90', label: 'Drops 90d', get: p => p.rank_drops_90 },
    { key: 'sellers', label: 'Sellers', get: p => p.sellers },
    { key: 'variations', label: 'Variations', get: p => p.variations },
    { key: 'amazon_oos_90', label: 'Amazon OOS % 90d', get: p => p.amazon_oos_90 },
];
const SELLER_SORTS = [
    ['default', 'Sort: as found'],
    ['sales_rank:asc', 'BSR: low → high'],
    ['monthly_sold:desc', 'Sold/mo: high → low'],
    ['rank_drops_30:desc', 'Drops 30d: high → low'],
    ['rank_drops_90:desc', 'Drops 90d: high → low'],
    ['sellers:asc', 'Sellers: low → high'],
    ['price:asc', 'Price: low → high'],
    ['price:desc', 'Price: high → low'],
    ['amazon_oos_90:desc', 'Amazon OOS%: high → low'],
    ['below_avg', 'Furthest below 90d avg price'],
];
const numOr = (x) => (x === '' || x === null || x === undefined || isNaN(Number(x)) ? null : Number(x));
const sellerRangesFromFilters = (f = {}) => {
    const out = {};
    (f?.page_filters || []).forEach(pf => {
        if (pf && SELLER_METRICS.some(m => m.key === pf.metric)) out[pf.metric] = { min: pf.min ?? '', max: pf.max ?? '' };
    });
    return out;
};
const rangeLabel = (m, r) => {
    const mn = numOr(r?.min), mx = numOr(r?.max);
    if (mn === null && mx === null) return null;
    if (mn !== null && mx !== null) return `${m.label}: ${mn}–${mx}`;
    return mn !== null ? `${m.label} ≥ ${mn}` : `${m.label} ≤ ${mx}`;
};

const EMPTY_VIEW = {
    gated: 'all', ranges: {}, category: '', brand: '',
    hideAmazon: false, hideVariations: false, belowAvg: false, hasUpc: false, hasSold: false, sort: 'default',
};

const viewGatedFor = (gating) => (gating === 'ungated' ? 'ungated' : gating === 'ungated_approval' ? 'ok' : 'all');

const initialTool = () => {
    try {
        const q = new URLSearchParams(window.location.search).get('tool');
        if (q === 'seller' || q === 'influencer') return q;
        return localStorage.getItem('preferredTool') === 'influencer' ? 'influencer' : 'seller';
    } catch { return 'seller'; }
};

// Plan access: Pro or higher.
// MSP GPT = Pro / Unlimited (bundle) or MSP_Pro / MSP_Unlimited
// MIP GPT = Pro / Unlimited (bundle) or MIP_Pro / MIP_Unlimited
const canUseTool = (user, t) => {
    if (!user?.subscription_active) return false;
    const plan = user?.subscription_plan || '';
    const prefix = t === 'influencer' ? 'MIP_' : 'MSP_';
    return ['Pro', 'Unlimited', `${prefix}Pro`, `${prefix}Unlimited`].includes(plan);
};

const MspGptPage = ({ user }) => {
    const [tool, setTool] = useState(() => {
        const t = initialTool();
        const other = t === 'influencer' ? 'seller' : 'influencer';
        return !canUseTool(user, t) && canUseTool(user, other) ? other : t;   // open the tool their plan includes
    });
    const [question, setQuestion] = useState('');
    const [gating, setGating] = useState(null);           // null until the user picks (or we detect it)
    const [choicePending, setChoicePending] = useState(null);   // question waiting for a gating choice
    const [marketPending, setMarketPending] = useState(null);   // question waiting for Amazon / Walmart
    const [micStop, setMicStop] = useState(0);                  // bumped on every Ask -> stops the mic
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [history, setHistory] = useState([]);
    const [remaining, setRemaining] = useState(null);
    const [limit, setLimit] = useState(10);
    const [active, setActive] = useState(null);           // current search (or { question, reply, notSearch, pending })
    const [products, setProducts] = useState([]);
    const [copiedAll, setCopiedAll] = useState(false);
    const [showRsHelp, setShowRsHelp] = useState(false);  // "how to reverse source" popup
    const [gatedMap, setGatedMap] = useState({});         // asin -> ungated | gated | approval | needs_login | error
    const [needsLogin, setNeedsLogin] = useState(false);
    const [view, setView] = useState(EMPTY_VIEW);
    const [resultsPage, setResultsPage] = useState(0);
    useEffect(() => { setResultsPage(0); }, [view, active?.id]);   // new filters / search -> page 1
    const [showSellerFilters, setShowSellerFilters] = useState(true);
    const gatingRunRef = useRef(0);
    const gatingQueuedRef = useRef(new Set());
    // Ungated mode: gate-check candidate ASINs BEFORE paying Keepa for product data
    const [scan, setScan] = useState(null);               // { running, checked, accepted, roundTotal, roundChecked, roundAccepted }
    const scanStopRef = useRef(false);
    const remainderRef = useRef([]);                      // unchecked candidates from the last round (sent back on "Check more")
    // Walmart mode: same Keepa search, each product is matched on Walmart by UPC (7 at a time)
    const [wmMap, setWmMap] = useState({});               // asin -> { status: 'loading' | 'found' | 'none', item }
    const wmQueuedRef = useRef(new Set());
    const wmChainRef = useRef(Promise.resolve());
    const isWalmartMode = active?.marketplace === 'walmart';

    const amazonDomain = authService.getAmazonMarketplace();
    const currency = authService.getAmazonCurrency();
    const amazonLinked = user?.amazon_linked_account && Object.keys(user.amazon_linked_account).length > 0;

    // Sidebar history — paginated, DB read only (no Keepa / Claude / daily search)
    const HISTORY_PAGE_SIZE = 15;
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyLoading, setHistoryLoading] = useState(false);

    const loadHistory = async (page = 1) => {
        setHistoryLoading(true);
        const { ok, data } = await api(`/api/msp-gpt/searches?page=${page}&page_size=${HISTORY_PAGE_SIZE}`);
        if (ok) {
            setHistory(data.searches || []);
            setHistoryPage(data.page || page);
            setHistoryTotal(data.total || 0);
            setRemaining(data.remaining);
            setLimit(data.limit || 10);
        }
        setHistoryLoading(false);
    };

    useEffect(() => { loadHistory(1); }, []);
    const historyPages = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));

    const updateRemaining = (data) => { if (data && data.remaining !== undefined) setRemaining(data.remaining); };

    // Fresh gating + view for every new / opened search
    const resetForSearch = (search) => {
        gatingRunRef.current += 1;
        gatingQueuedRef.current = new Set();
        scanStopRef.current = true;
        remainderRef.current = [];
        setScan(null);
        setGatedMap({});
        setWmMap({});
        wmQueuedRef.current = new Set();
        wmChainRef.current = Promise.resolve();
        setNeedsLogin(false);
        setView({ ...EMPTY_VIEW, gated: viewGatedFor(search?.gating), ranges: sellerRangesFromFilters(search?.filters) });
    };

    const newChat = () => {
        resetForSearch(null);
        setActive(null);
        setProducts([]);
        setError('');
        setQuestion('');
        setGating(null);
        setChoicePending(null);
        setMarketPending(null);
    };

    const switchTool = (t) => {
        if (t === tool) return;
        setTool(t);
        newChat();
    };

    // ── Gating check for normal-mode results: batches of 10, one after another ──
    const checkGating = async (asins, runId) => {
        for (let i = 0; i < asins.length; i += 10) {
            if (gatingRunRef.current !== runId) return;
            const batch = asins.slice(i, i + 10);
            let results = {};
            try {
                const { data } = await postJson('/api/a2a/check-gated-bulk', { asins: batch, skip_cache: false });
                results = data.results || {};
            } catch { /* marked as error below */ }
            if (gatingRunRef.current !== runId) return;
            setGatedMap(prev => {
                const next = { ...prev };
                batch.forEach(a => { next[a] = results[a] || 'error'; });
                return next;
            });
            if (Object.values(results).includes('needs_login')) { setNeedsLogin(true); return; }
        }
    };

    useEffect(() => {
        if (!amazonLinked || products.length === 0 || active?.tool === 'influencer' || active?.marketplace === 'walmart') return;
        const todo = products.map(p => p.asin).filter(a => !gatingQueuedRef.current.has(a));
        if (!todo.length) return;
        todo.forEach(a => gatingQueuedRef.current.add(a));
        checkGating(todo, gatingRunRef.current);
    }, [products, amazonLinked, active?.tool, active?.marketplace]);

    const recheckOne = (asin) => {
        setGatedMap(prev => { const n = { ...prev }; delete n[asin]; return n; });
        checkGating([asin], gatingRunRef.current);
    };

    // ── Walmart mode: UPC lookups, 7 at a time with a pause. No UPC / no match -> skipped (name search stays manual) ──
    const scanWalmart = async (list, runId) => {
        const hasUpc = (p) => /^\d{8,14}$/.test(p.upc || '');
        const skip = list.filter(p => !hasUpc(p));
        if (skip.length) setWmMap(prev => { const n = { ...prev }; skip.forEach(p => { n[p.asin] = { status: 'none' }; }); return n; });
        const todo = list.filter(hasUpc);
        for (let i = 0; i < todo.length; i += WM_BATCH) {
            if (gatingRunRef.current !== runId) return;
            const batch = todo.slice(i, i + WM_BATCH);
            setWmMap(prev => { const n = { ...prev }; batch.forEach(p => { n[p.asin] = { status: 'loading' }; }); return n; });
            const results = await Promise.all(batch.map(p => walmartSearch(p.upc, 'upc')));
            if (gatingRunRef.current !== runId) return;
            setWmMap(prev => {
                const n = { ...prev };
                batch.forEach((p, k) => { const item = results[k][0]; n[p.asin] = item ? { status: 'found', item } : { status: 'none' }; });
                return n;
            });
            if (i + WM_BATCH < todo.length) await sleep(wmPause());
        }
    };

    useEffect(() => {
        if (!isWalmartMode || !products.length) return;
        const todo = products.filter(p => !wmQueuedRef.current.has(p.asin));
        if (!todo.length) return;
        todo.forEach(p => wmQueuedRef.current.add(p.asin));
        const runId = gatingRunRef.current;
        wmChainRef.current = wmChainRef.current.then(() => scanWalmart(todo, runId)).catch(() => { });   // "Load more" waits its turn
    }, [products, isWalmartMode]);

    // ── Ungated mode round: gate-check candidates (0 Keepa tokens), then product data ONLY for accepted ones ──
    const runUngatedRound = async (search, candidates) => {
        const runId = gatingRunRef.current;
        const accept = search.gating === 'ungated_approval' ? ['ungated', 'approval'] : ['ungated'];
        scanStopRef.current = false;
        candidates.forEach(a => gatingQueuedRef.current.add(a));   // the products effect must not re-check these
        setScan(prev => ({
            checked: prev?.checked || 0, accepted: prev?.accepted || 0,
            running: true, roundTotal: candidates.length, roundChecked: 0, roundAccepted: 0,
        }));

        let roundAccepted = 0;
        let stoppedAt = candidates.length;
        // Product requests run one at a time in the background (the server updates its per-round
        // counter on each call), while the gating scan keeps going. Keepa charges per ASIN,
        // so sending them as soon as they're found costs exactly the same tokens.
        let chain = Promise.resolve();
        const queueProducts = (asins) => {
            if (!asins.length) return;
            chain = chain.then(() => fetchProducts(asins)).catch(() => { });
        };

        const fetchProducts = async (asins) => {
            if (gatingRunRef.current !== runId) return;
            const { ok, data } = await postJson(`/api/msp-gpt/searches/${search.id}/products`, { asins });
            if (gatingRunRef.current !== runId) return;
            if (ok) {
                setProducts(prev => {
                    const have = new Set(prev.map(p => p.asin));
                    return [...prev, ...(data.products || []).filter(p => !have.has(p.asin))];
                });
                setActive(data.search);
                setHistory(prev => prev.map(h => h.id === data.search.id ? { ...h, product_count: data.search.product_count } : h));
            } else {
                setError(data.message || data.error || 'Failed to load products.');
            }
        };

        for (let i = 0; i < candidates.length; i += 10) {
            if (gatingRunRef.current !== runId) return;
            if (scanStopRef.current) { stoppedAt = i; break; }
            const batch = candidates.slice(i, i + 10);
            let results = {};
            try {
                const { data } = await postJson('/api/a2a/check-gated-bulk', { asins: batch, skip_cache: false });
                results = data.results || {};
            } catch { /* treated as not accepted */ }
            if (gatingRunRef.current !== runId) return;

            setGatedMap(prev => {
                const next = { ...prev };
                batch.forEach(a => { next[a] = results[a] || 'error'; });
                return next;
            });
            if (Object.values(results).includes('needs_login')) { setNeedsLogin(true); stoppedAt = i + 10; break; }

            const ok = batch.filter(a => accept.includes(results[a])).slice(0, ROUND_TARGET - roundAccepted);
            roundAccepted += ok.length;
            queueProducts(ok);                                // show them right away, don't wait for the scan
            setScan(prev => ({
                ...prev,
                checked: (prev?.checked || 0) + batch.length,
                accepted: (prev?.accepted || 0) + ok.length,
                roundChecked: Math.min(i + 10, candidates.length),
                roundAccepted,
            }));

            if (roundAccepted >= ROUND_TARGET) { stoppedAt = i + 10; break; }
        }
        await chain;                                          // let the last product requests finish
        if (gatingRunRef.current !== runId) return;
        remainderRef.current = candidates.slice(stoppedAt);
        console.log(`[MSP GPT] round done: ${candidates.length} candidates, checked ${Math.min(stoppedAt, candidates.length)}, `
            + `accepted ${roundAccepted} (${accept.join('+')}), left for "Check more": ${remainderRef.current.length}`);
        setScan(prev => ({ ...prev, running: false }));
    };

    // ── MIP GPT round ─────────────────────────────────────────────────────────
    //  1. FAST  : card-data-keepa, 7 at a time -> cards appear right away (image, title, sales, price, CC).
    //  2. FULL  : products without real video data load live card-data in the background,
    //             7 at a time with a short pause; their boxes show "Loading full data…" meanwhile.
    //  Video limits are applied on the real data; a round stops at 100 matching products.
    const runInfluencerRound = async (search, candidates) => {
        const runId = gatingRunRef.current;
        const alive = () => gatingRunRef.current === runId;
        const ccOnly = search.gating === 'cc_only' || !!search.filters?.has_cc;
        const ranges = rangesFromFilters(search.filters);   // min/max the user asked for (page filters)
        const limited = hasRanges(ranges);
        const waitFull = needsFullData(ranges);              // video / income / breakeven limits need the full data
        const check = (r, full) => passesRanges(r, deriveInfluencer(r), ranges, full);
        scanStopRef.current = false;
        setScan(prev => ({
            checked: prev?.checked || 0, accepted: prev?.accepted || 0, fullLeft: 0,
            running: true, roundTotal: candidates.length, roundChecked: 0, roundAccepted: 0,
        }));

        const recs = new Map();       // asin -> latest record
        const queue = [];             // asins waiting for full data
        const done = new Set();       // candidates already processed
        let roundChecked = 0;
        let accepted = 0;             // products that count for this round
        let pending = 0;              // shown, waiting for full data to confirm the limits
        let producerDone = false;

        const tick = (dChecked, dAccepted) => setScan(prev => ({
            ...prev,
            checked: (prev?.checked || 0) + dChecked,
            accepted: (prev?.accepted || 0) + dAccepted,
            roundChecked, roundAccepted: accepted, fullLeft: queue.length,
        }));

        let saveChain = Promise.resolve();
        const save = (rows) => {
            if (!rows.length) return;
            saveChain = saveChain.then(async () => {
                if (!alive()) return;
                const { ok, data } = await postJson(`/api/msp-gpt/searches/${search.id}/save-results`, { products: rows });
                if (!alive() || !ok) return;
                setActive(data.search);
                setHistory(prev => prev.map(h => h.id === data.search.id ? { ...h, product_count: data.search.product_count } : h));
            }).catch(() => { });
        };

        const full = () => accepted + pending >= ROUND_TARGET;

        const producer = async () => {
            for (let i = 0; i < candidates.length; i += CARD_BATCH) {
                while (alive() && !scanStopRef.current && full() && pending > 0) await sleep(400);   // wait: rejections free room
                if (!alive() || scanStopRef.current || full()) break;
                const batch = candidates.slice(i, i + CARD_BATCH);
                const results = await Promise.all(batch.map(a => loadQuick(a, ccOnly)));
                if (!alive()) return;
                batch.forEach(a => done.add(a));
                roundChecked += batch.length;
                const show = [];
                const finals = [];
                let dAcc = 0;
                results.forEach(r => {
                    if (!r || full()) return;
                    recs.set(r.asin, r);
                    if (r.cardStatus === 'live') {
                        if (limited && !check(r, true)) return;          // real data says no
                        accepted += 1; dAcc += 1;
                        show.push(r); finals.push(r);
                    } else {
                        if (limited && !check(r, false)) return;         // what we already know says no
                        queue.push(r.asin);
                        show.push(r);
                        if (waitFull) pending += 1; else { accepted += 1; dAcc += 1; }
                    }
                });
                if (show.length) setProducts(prev => {
                    const have = new Set(prev.map(p => p.asin));
                    return [...prev, ...show.filter(r => !have.has(r.asin))];
                });
                save(finals);
                tick(batch.length, dAcc);
                if (i + CARD_BATCH < candidates.length) await sleep(CARD_BATCH_PAUSE_MS);   // don't flood the backend / Keepa
            }
            producerDone = true;
        };

        const consumer = async () => {
            while (alive()) {
                if (scanStopRef.current) return;
                if (!queue.length) {
                    if (producerDone) return;
                    await sleep(300);
                    continue;
                }
                const batch = queue.splice(0, CARD_BATCH);
                const cards = await Promise.all(batch.map(a => fetchCardData(a)));
                if (!alive()) return;
                const merged = new Map();
                const finals = [];
                let dAcc = 0;
                batch.forEach((a, k) => {
                    const m = mergeCard(recs.get(a), cards[k]);
                    recs.set(a, m);
                    merged.set(a, m);
                    if (waitFull) {
                        pending -= 1;
                        if (m.cardStatus === 'live' && check(m, true)) { accepted += 1; dAcc += 1; finals.push(m); }
                    } else {
                        finals.push(m);
                    }
                });
                setProducts(prev => prev.map(p => merged.get(p.asin) || p));
                save(finals);
                tick(0, dAcc);
                await sleep(CARD_BATCH_PAUSE_MS);
            }
        };

        await Promise.all([producer(), consumer()]);
        if (!alive()) return;
        if (queue.length) {                                   // stopped: these didn't get full data
            const rest = new Set(queue);
            setProducts(prev => prev.map(p => (rest.has(p.asin) && p.cardStatus === 'loading' ? { ...p, cardStatus: 'stopped' } : p)));
        }
        await saveChain;
        if (!alive()) return;
        remainderRef.current = candidates.filter(a => !done.has(a));
        console.log(`[MSP GPT] MIP round done: ${candidates.length} ASINs, checked ${roundChecked}, accepted ${accepted} `
            + `(ccOnly=${ccOnly}, ranges=${JSON.stringify(ranges)}), left: ${remainderRef.current.length}`);
        setScan(prev => ({ ...prev, running: false, fullLeft: 0 }));
    };

    // Retry live card data for one product (card button). Saves it if it wasn't saved yet.
    const retryCard = async (asin) => {
        const searchId = active?.id;
        const current = products.find(p => p.asin === asin);
        if (!current) return;
        const card = await fetchCardData(asin);
        const merged = mergeCard(current, card);
        setProducts(prev => prev.map(p => (p.asin === asin ? merged : p)));
        if (searchId && card.cardStatus === 'live') {
            postJson(`/api/msp-gpt/searches/${searchId}/save-results`, { products: [merged] }).catch(() => { });
        }
    };

    const runRound = (search, candidates) => (search?.mode === 'influencer'
        ? runInfluencerRound(search, candidates)
        : runUngatedRound(search, candidates));

    // ── "Check more products" / Influencer "Load more" — uses 1 search ──
    const checkMore = async () => {
        if (!active?.id || scan?.running) return;
        setError('');
        const search = active;
        setScan(prev => ({ ...(prev || { checked: 0, accepted: 0 }), running: true, roundTotal: 0, roundChecked: 0, roundAccepted: 0 }));
        const { ok, status, data } = await postJson(`/api/msp-gpt/searches/${search.id}/candidates`, { remainder: remainderRef.current });
        updateRemaining(data);
        if (!ok) {
            setError(data.message || data.error || 'Failed to find more products.');
            if (status === 429) setRemaining(0);
            setScan(prev => ({ ...prev, running: false }));
            return;
        }
        remainderRef.current = [];
        setActive(data.search);
        if (!(data.candidates || []).length) { setScan(prev => ({ ...prev, running: false })); return; }
        runRound(data.search, data.candidates);
    };

    const stopScan = () => { scanStopRef.current = true; };

    // ── Ask a new question, or refine the open search ──
    const ask = async (text, chosenGating, chosenMarket) => {
        setMicStop(n => n + 1);
        const q = (text ?? question).trim();
        if (q.length < 2 || loading) return;
        const refineOf = active?.id && !active.notSearch ? active.id : null;

        const market = refineOf ? null : tool === 'influencer' ? 'amazon' : (chosenMarket || detectMarketplace(q));
        if (!refineOf && !market) { setChoicePending(null); setMarketPending(q); return; }   // ask Amazon or Walmart
        setMarketPending(null);
        let g = market === 'walmart' ? 'all' : (chosenGating || gating || detectGating(q, tool));
        if (!g && !refineOf) { setChoicePending(q); return; }   // ask which results they want
        setChoicePending(null);
        if (g && market !== 'walmart') setGating(g);

        setLoading(true);
        setError('');
        const prevQuestion = refineOf ? active.question : null;
        resetForSearch(null);
        setActive({ question: q, pending: true, parent_question: prevQuestion });
        setProducts([]);
        try {
            const body = { question: q, gating: g || undefined, tool, marketplace: market || undefined };
            if (refineOf) body.refine_of = refineOf;
            const { ok, status, data } = await postJson('/api/msp-gpt/search', body);
            updateRemaining(data);
            if (!ok) {
                setActive(null);
                setError(data.message || data.error || 'Something went wrong.');
                if (status === 429) setRemaining(0);
                return;
            }
            if (data.not_search) {
                setActive({ question: q, reply: data.reply, notSearch: true });
                return;
            }
            resetForSearch(data.search);
            setActive(data.search);
            setGating(data.search.gating);
            setProducts(data.products || []);
            setQuestion('');
            loadHistory(1);
            if (data.search?.mode === 'ungated' || data.search?.mode === 'influencer') runRound(data.search, data.candidates || []);
        } catch {
            setActive(null);
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const openSearch = async (id) => {
        setError('');
        setMarketPending(null);
        setChoicePending(null);
        resetForSearch(null);
        setActive({ pending: true, question: history.find(h => h.id === id)?.question });
        setProducts([]);
        const { ok, data } = await api(`/api/msp-gpt/searches/${id}`);
        if (ok) {
            resetForSearch(data.search);
            setTool(data.search.tool || 'seller');
            setActive(data.search);
            setGating(data.search.gating);
            setProducts(data.products || []);
        } else {
            setActive(null);
            setError(data.error || 'Failed to load search.');
        }
    };

    // ── "Load 100 more" (normal mode) — uses 1 search ──
    const loadMore = async () => {
        if (!active?.id || loadingMore) return;
        setLoadingMore(true);
        setError('');
        const { ok, status, data } = await postJson(`/api/msp-gpt/searches/${active.id}/more`);
        updateRemaining(data);
        if (ok) {
            setProducts(prev => [...prev, ...(data.products || [])]);
            setActive(data.search);
            setHistory(prev => prev.map(h => h.id === data.search.id ? { ...h, product_count: data.search.product_count } : h));
        } else {
            setError(data.message || data.error || 'Failed to load more.');
            if (status === 429) setRemaining(0);
        }
        setLoadingMore(false);
    };

    // ── Counts + client-side filters ──
    const counts = useMemo(() => {
        const c = { all: products.length, ungated: 0, approval: 0, gated: 0, checking: 0 };
        products.forEach(p => {
            const s = gatedMap[p.asin];
            if (s === 'ungated' || s === 'approval' || s === 'gated') c[s] += 1;
            else if (!s) c.checking += 1;
        });
        c.ok = c.ungated + c.approval;
        return c;
    }, [products, gatedMap]);

    const categories = useMemo(() => [...new Set(products.map(p => p.category).filter(Boolean))].sort(), [products]);

    const visible = useMemo(() => {
        const brandQ = view.brand.trim().toLowerCase();
        const list = products.filter(p => {
            const s = gatedMap[p.asin];
            if (view.gated === 'ok' && !(s === 'ungated' || s === 'approval')) return false;
            if (!['all', 'ok'].includes(view.gated) && s !== view.gated) return false;
            for (const m of SELLER_METRICS) {
                const r = view.ranges[m.key];
                const mn = numOr(r?.min), mx = numOr(r?.max);
                if (mn === null && mx === null) continue;
                const val = m.get(p);
                if (val === null || val === undefined || isNaN(val)) return false;
                if (mn !== null && val < mn) return false;
                if (mx !== null && val > mx) return false;
            }
            if (view.category && p.category !== view.category) return false;
            if (brandQ && !(p.brand || '').toLowerCase().includes(brandQ)) return false;
            if (view.hideAmazon && p.amazon_selling) return false;
            if (view.hideVariations && p.variations > 1) return false;
            if (view.belowAvg && !(p.price && p.price_avg90 && p.price < p.price_avg90)) return false;
            if (view.hasUpc && !p.upc) return false;
            if (view.hasSold && !p.monthly_sold) return false;
            return true;
        });
        if (view.sort === 'below_avg') {
            return [...list].sort((a, b) => ((a.price_avg90 ? a.price / a.price_avg90 : 9) - (b.price_avg90 ? b.price / b.price_avg90 : 9)));
        }
        const [key, dir] = view.sort.split(':');
        const m = SELLER_METRICS.find(x => x.key === key);
        if (!m) return list;
        const k = dir === 'asc' ? 1 : -1;
        return [...list].sort((a, b) => {
            const av = m.get(a), bv = m.get(b);
            const an = av === null || av === undefined, bn = bv === null || bv === undefined;
            if (an && bn) return 0;
            if (an) return 1;
            if (bn) return -1;
            return av < bv ? -k : av > bv ? k : 0;
        });
    }, [products, gatedMap, view]);

    const sellerActiveRanges = SELLER_METRICS.map(m => ({ m, text: rangeLabel(m, view.ranges[m.key]) })).filter(x => x.text);
    const setRange = (k, side, val) => setView(prev => ({
        ...prev, ranges: { ...prev.ranges, [k]: { ...(prev.ranges[k] || { min: '', max: '' }), [side]: val } },
    }));
    const clearRange = (k) => setView(prev => { const r = { ...prev.ranges }; delete r[k]; return { ...prev, ranges: r }; });

    // Walmart mode exports / copies the matched rows, Amazon mode the visible (filtered) rows
    const exportList = isWalmartMode ? products.filter(p => wmMap[p.asin]?.status === 'found') : visible;
    const wmStats = useMemo(() => {
        let found = 0, pending = 0, none = 0;
        products.forEach(p => { const s = wmMap[p.asin]?.status; if (s === 'found') found += 1; else if (s === 'none') none += 1; else pending += 1; });
        return { found, pending, none };
    }, [products, wmMap]);

    const exportCsv = () => {
        const wmItem = (p) => wmMap[p.asin]?.item || {};
        const cols = [
            ...(isWalmartMode ? [['Walmart Item ID', p => wmItem(p).item_id]] : []),   // Walmart: item ID first for Reverse Scan
            ['ASIN', p => p.asin], ['Title', p => p.title], ['Brand', p => p.brand], ['UPC', p => p.upc],
            ['Category', p => p.category], ['Gated', p => gatedMap[p.asin] || ''],
            ['Price', p => p.price], ['Price 90d Avg', p => p.price_avg90],
            ['BSR', p => p.sales_rank], ['BSR 90d Avg', p => p.bsr_avg90],
            ['Drops 30d', p => p.rank_drops_30], ['Drops 90d', p => p.rank_drops_90],
            ['Sold / mo', p => p.monthly_sold], ['Sellers', p => p.sellers],
            ['Amazon Selling', p => (p.amazon_selling ? 'Yes' : 'No')], ['Variations', p => p.variations],
            ['Amazon OOS % 90d', p => p.amazon_oos_90], ['Amazon URL', p => `https://www.amazon${amazonDomain}/dp/${p.asin}`],
            ...(isWalmartMode ? [
                ['Walmart Price', p => { const v = parseFloat(wmItem(p).price_value ?? wmItem(p).price); return isNaN(v) ? '' : v; }],
                ['Walmart Seller', p => wmItem(p).seller_name || wmItem(p).sellerName],
                ['Walmart URL', p => (wmItem(p).item_id ? `https://www.walmart.com/ip/${wmItem(p).item_id}` : '')],
            ] : []),
        ];
        const esc = (v) => (v === null || v === undefined ? '' : `"${String(v).replace(/"/g, '""')}"`);
        const csv = [cols.map(c => esc(c[0])).join(','), ...exportList.map(p => cols.map(c => esc(c[1](p))).join(','))].join('\n');
        const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(active?.question || 'msp_gpt').replace(/[^a-z0-9]+/gi, '_').slice(0, 60)}.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        setShowRsHelp(true);
    };

    const copyAllAsins = () => {
        navigator.clipboard.writeText(exportList.map(p => p.asin).join('\n'));
        setCopiedAll(true);
        setTimeout(() => setCopiedAll(false), 1500);
    };

    const outOfSearches = remaining === 0;
    const isUngatedMode = active?.mode === 'ungated';
    const isRefine = !!(active?.id && !active.notSearch && !active.pending);
    const acceptedWord = active?.gating === 'ungated_approval' ? 'ungated or approval' : 'ungated';
    const isInfluencer = active?.tool === 'influencer' || (!active && tool === 'influencer');
    const isRoundMode = isUngatedMode || active?.mode === 'influencer';
    const ccOnly = active?.gating === 'cc_only' || !!active?.filters?.has_cc;
    const videoLimited = hasRanges(rangesFromFilters(active?.filters));
    const infFiltering = ccOnly || videoLimited;          // the page filters results itself -> "Check more"
    const matchWord = ccOnly && videoLimited ? 'with live CC matching your filters' : ccOnly ? 'with live CC' : 'matching your filters';
    const scanLabel = active?.mode === 'influencer'
        ? (ccOnly ? 'Finding products with Creator Connections…' : 'Finding products…')
        : 'Checking your gated status…';
    const acceptedLabel = active?.mode === 'influencer' ? (infFiltering ? matchWord : 'loaded') : acceptedWord;
    const moreLabel = active?.mode === 'influencer' && !infFiltering ? 'Load 100 more' : 'Check more products';
    const gatingOptions = tool === 'influencer' ? INF_GATING_OPTIONS : GATING_OPTIONS;
    const setV = (patch) => setView(prev => ({ ...prev, ...patch }));
    const inputCls = 'w-full min-w-0 px-2 py-1 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-teal-400';
    const checkFilter = (label, k) => (
        <label key={k} className="flex items-center gap-1 cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={view[k]} onChange={e => setV({ [k]: e.target.checked })} className="accent-teal-600" /> {label}
        </label>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            <style>{`
                @keyframes mspShine { 0% { transform: translateX(-150%) skewX(-20deg); } 100% { transform: translateX(450%) skewX(-20deg); } }
                .msp-shine { animation: mspShine 2.8s ease-in-out infinite; }
            `}</style>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                            <Sparkles className={tool === 'influencer' ? 'text-blue-600' : 'text-teal-600'} size={36} /> {tool === 'influencer' ? 'MIP GPT' : 'MSP GPT'}
                            <LiveBadge />
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Describe the products you want. We'll find them for you.
                        </p>
                        <div className="mt-3"><ToolSwitch value={tool} onChange={switchTool} /></div>
                    </div>
                    {remaining !== null && (
                        <span
                            className={`px-4 py-2 rounded-full text-sm font-semibold ${outOfSearches ? 'bg-gray-200 text-gray-600' : 'bg-gradient-to-r from-teal-500 to-blue-600 text-white'}`}
                            title="Every question, refine, Load more and Check more uses 1 search"
                        >
                            {remaining} / {limit} searches left today
                        </span>
                    )}
                </div>

                {!canUseTool(user, tool) ? (
                    <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-lg mx-auto">
                        <h2 className="text-xl font-bold text-gray-800">{tool === 'influencer' ? 'MIP GPT' : 'MSP GPT'} is not in your plan</h2>
                        <p className="text-gray-600 mt-2 text-sm">
                            It's included in the {tool === 'influencer' ? 'MIP Pro' : 'MSP Pro'} plan and the Pro bundle.
                        </p>
                        <a href="/pricing" className="inline-block mt-5 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-blue-600 text-white font-bold shadow-lg hover:opacity-90">Upgrade</a>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-[260px_1fr] gap-6">
                        {/* History */}
                        <aside className="bg-white rounded-xl shadow-lg p-4 h-fit lg:sticky lg:top-20 order-2 lg:order-1">
                            <button
                                onClick={newChat}
                                className="w-full mb-3 flex items-center justify-center gap-2 px-3 py-2 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-sm font-semibold hover:bg-teal-100"
                            >
                                <Plus size={16} /> New chat
                            </button>
                            <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                                <History size={12} /> Your searches
                            </div>
                            {history.length === 0 ? (
                                <p className="text-sm text-gray-400">No searches yet.</p>
                            ) : (
                                <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
                                    {history.map(h => (
                                        <button
                                            key={h.id}
                                            onClick={() => openSearch(h.id)}
                                            className={`w-full text-left px-2 py-2 rounded-lg text-sm transition ${active?.id === h.id ? 'bg-teal-50 text-teal-800' : 'hover:bg-gray-50 text-gray-700'}`}
                                        >
                                            <div className="line-clamp-2">{h.question}</div>
                                            <span className={`inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${h.filters?.tool === 'influencer' ? 'bg-blue-50 text-blue-700' : 'bg-teal-50 text-teal-700'}`}>
                                                {h.filters?.tool === 'influencer' ? 'MIP GPT' : 'MSP GPT'}
                                            </span>
                                            <div className="text-[11px] text-gray-400 mt-0.5">
                                                {new Date(h.created_at).toLocaleDateString()} · {h.product_count} products
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {historyPages > 1 && (
                                <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-100">
                                    <button
                                        onClick={() => loadHistory(historyPage - 1)}
                                        disabled={historyPage <= 1 || historyLoading}
                                        className="px-2 py-1 text-xs font-semibold bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-40"
                                    >
                                        ← Prev
                                    </button>
                                    <span className="text-[11px] text-gray-500 flex items-center gap-1">
                                        {historyLoading && <Loader2 size={11} className="animate-spin" />}
                                        {historyPage} / {historyPages}
                                    </span>
                                    <button
                                        onClick={() => loadHistory(historyPage + 1)}
                                        disabled={historyPage >= historyPages || historyLoading}
                                        className="px-2 py-1 text-xs font-semibold bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-40"
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}
                        </aside>

                        {/* Main */}
                        <main className="order-1 lg:order-2 min-w-0">
                            {/* Ask / refine box */}
                            {!active?.pending && (
                                <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
                                    {isRefine && (
                                        <div className="flex items-center justify-between gap-2 mb-2">
                                            <span className="text-xs font-bold text-teal-700 flex items-center gap-1"><Wand2 size={13} /> Refine these results</span>
                                            <button onClick={newChat} className="text-xs font-semibold text-gray-500 hover:text-gray-800 flex items-center gap-1"><Plus size={13} /> New chat</button>
                                        </div>
                                    )}
                                    <div className="flex gap-2 items-end">
                                        <textarea
                                            rows={2}
                                            value={question}
                                            onChange={(e) => setQuestion(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
                                            disabled={loading || outOfSearches}
                                            maxLength={500}
                                            placeholder={outOfSearches
                                                ? 'You used all your searches for today. See you tomorrow!'
                                                : isRefine
                                                    ? 'Change something, e.g. "only under $30" or "fewer videos on the listing" (uses 1 search)'
                                                    : tool === 'influencer'
                                                        ? 'e.g. Kitchen gadgets under $40 selling 300+ a month with few videos on the listing'
                                                        : 'e.g. Toys between $15 and $35 with 300+ monthly sales and no Amazon offer'}
                                            className="flex-1 resize-none px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm disabled:bg-gray-50"
                                        />
                                        <MicButton value={question} onChange={setQuestion} disabled={loading || outOfSearches} stopKey={micStop} />
                                        <button
                                            onClick={() => ask()}
                                            disabled={loading || outOfSearches || question.trim().length < 2}
                                            className="h-12 px-5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {loading ? <RefreshCw className="animate-spin" size={18} /> : isRefine ? <Wand2 size={18} /> : <Send size={18} />}
                                            {isRefine ? 'Refine' : 'Ask'}
                                        </button>
                                    </div>
                                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                                        <GatingPicker value={gating} onChange={setGating} disabled={loading || outOfSearches} options={gatingOptions} />
                                        <span className="text-[11px] text-gray-400 flex items-center gap-1">
                                            <Shield size={11} />
                                            {tool === 'influencer'
                                                ? 'Only with Creator Connections checks campaigns first, so you only load products worth filming.'
                                                : 'Ungated searches check your gating first, so you get sellable leads faster.'}
                                        </span>
                                    </div>

                                    {marketPending && (
                                        <div className="mt-3 border-2 border-teal-200 bg-teal-50/50 rounded-xl p-3">
                                            <p className="text-sm font-bold text-gray-800 mb-2">Where do you want to sell?</p>
                                            <div className="grid gap-2 sm:grid-cols-2">
                                                {[
                                                    ['amazon', 'Amazon', 'Amazon products + your gated status.', <img src="/icons/amazon.png" alt="" className="w-5 h-5 object-contain" />],
                                                    ['walmart', 'Walmart', 'We find the same products on Walmart (UPC match).', <WmLogo className="w-5 h-5" />],
                                                ].map(([key, label, desc, icon]) => (
                                                    <button key={key} onClick={() => ask(marketPending, undefined, key)}
                                                        className="text-left p-3 rounded-lg border-2 border-gray-200 bg-white hover:border-teal-500 transition">
                                                        <div className="text-sm font-bold text-gray-800 flex items-center gap-2">{icon} {label}</div>
                                                        <div className="text-[11px] text-gray-500 mt-0.5">{desc}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {choicePending && (
                                        <div className="mt-3 border-2 border-teal-200 bg-teal-50/50 rounded-xl p-3">
                                            <p className="text-sm font-bold text-gray-800 mb-2">What would you like us to find?</p>
                                            <div className={`grid gap-2 ${gatingOptions.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
                                                {gatingOptions.map(o => (
                                                    <button
                                                        key={o.key}
                                                        onClick={() => ask(choicePending, o.key, 'amazon')}
                                                        className={`text-left p-3 rounded-lg border-2 bg-white hover:border-teal-500 transition ${o.key === 'ungated' || o.key === 'cc_only' ? 'border-teal-400' : 'border-gray-200'}`}
                                                    >
                                                        <div className="text-sm font-bold text-gray-800">{o.key === 'cc_only' ? '🤝 ' : o.key !== 'all' ? '🛡 ' : ''}{o.label}</div>
                                                        <div className="text-[11px] text-gray-500 mt-0.5">{o.desc}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {!active && !outOfSearches && !choicePending && !marketPending && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {(tool === 'influencer' ? INF_EXAMPLES : EXAMPLES).map(ex => (
                                                <button
                                                    key={ex}
                                                    onClick={() => ask(ex)}
                                                    disabled={loading}
                                                    className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-teal-50 hover:text-teal-700 text-slate-600 rounded-full border border-slate-200 transition text-left"
                                                >
                                                    {ex}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {error && (
                                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-center gap-2">
                                    <AlertCircle size={16} /> {error}
                                </div>
                            )}

                            {!isInfluencer && !isWalmartMode && (!amazonLinked || needsLogin) && (products.length > 0 || isUngatedMode) && (
                                <div className="mb-4 bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3 text-sm flex flex-wrap items-center gap-3">
                                    <span className="text-yellow-800 font-semibold">Link your Amazon account to see your gated status on every product.</span>
                                    <button onClick={() => window.open(LWA_URL(), '_blank')} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
                                        <img src="https://images-na.ssl-images-amazon.com/images/G/01/lwa/btnLWA_gold_101x46.png" style={{ height: 25 }} alt="Link Amazon Account" />
                                    </button>
                                </div>
                            )}

                            {/* Conversation */}
                            {active && (
                                <div className="space-y-4">
                                    {active.question && (
                                        <div className="flex flex-col items-end gap-1">
                                            {active.parent_question && (
                                                <span className="text-[11px] text-gray-500 bg-white border border-gray-200 rounded-full px-2 py-0.5 max-w-[85%] truncate">
                                                    Refined from: {active.parent_question}
                                                </span>
                                            )}
                                            <div className="bg-teal-600 text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[85%] text-sm shadow">
                                                {active.question}
                                            </div>
                                        </div>
                                    )}

                                    <div className="bg-white rounded-2xl rounded-bl-sm shadow-lg p-4">
                                        {active.pending ? (
                                            <div className="flex items-center gap-3 text-gray-600 text-sm py-2">
                                                <RefreshCw className="animate-spin text-teal-600" size={18} />
                                                Searching millions of Amazon products for you…
                                            </div>
                                        ) : active.notSearch ? (
                                            <p className="text-gray-700 text-sm">{active.reply}</p>
                                        ) : (
                                            <>
                                                <div className="flex flex-wrap items-start justify-between gap-3">
                                                    <div>
                                                        <p className="text-lg font-bold text-gray-800">
                                                            {products.length > 0
                                                                ? '🎉 We found the products for you!'
                                                                : isRoundMode && scan?.running
                                                                    ? (active.mode === 'influencer'
                                                                        ? (infFiltering ? `🎬 Finding products ${matchWord}…` : '🎬 Loading your products…')
                                                                        : `🛡 Finding products you're ${acceptedWord} for…`)
                                                                    : isRoundMode
                                                                        ? (active.mode === 'influencer'
                                                                            ? (infFiltering ? `No products ${matchWord} found yet.` : 'No products loaded yet.')
                                                                            : `No ${acceptedWord} products found yet.`)
                                                                        : 'No products matched this question.'}
                                                        </p>
                                                        <p className="text-sm text-gray-600 mt-0.5">
                                                            {active.reply}
                                                            {isRoundMode
                                                                ? products.length > 0 && <> · showing <strong>{products.length}</strong> {active.mode === 'influencer' ? (infFiltering ? `products ${matchWord}` : 'products') : `${acceptedWord} products`}</>
                                                                : active.total_results > 0 && <> · <strong>{active.total_results.toLocaleString()}</strong> matches, showing {products.length}</>}
                                                        </p>
                                                    </div>
                                                    {!isInfluencer && exportList.length > 0 && (
                                                        <div className="flex flex-col items-end gap-1">
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={copyAllAsins}
                                                                    className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50"
                                                                    title="Paste them into Reverse Scan → Paste ASINs"
                                                                >
                                                                    {copiedAll ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                                                                    {copiedAll ? 'Copied!' : `Copy ${exportList.length} ASINs`}
                                                                </button>
                                                                <button
                                                                    onClick={exportCsv}
                                                                    title="Download a CSV, then upload it on the Reverse Scan page to find suppliers"
                                                                    className="relative overflow-hidden flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-amber-500 to-orange-600 shadow-lg ring-2 ring-amber-300 hover:opacity-90"
                                                                >
                                                                    <span className="msp-shine pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
                                                                    <Download size={14} /> Export {exportList.length} to CSV
                                                                </button>
                                                            </div>
                                                            <button onClick={() => setShowRsHelp(true)} className="text-[11px] text-teal-700 hover:underline font-semibold">
                                                                Upload the CSV on Reverse Scan to find suppliers →
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap gap-1.5 mt-3">
                                                    {filterChips(active.filters, currency).map((c, i) => (
                                                        <span key={`${c.kind}-${i}`} title={c.kind === 'unsupported' ? 'We have no data to filter this' : undefined}
                                                            className={`text-[11px] border px-2 py-0.5 rounded-full font-semibold ${CHIP_CLS[c.kind]}`}>{CHIP_PREFIX[c.kind]}{c.text}</span>
                                                    ))}
                                                </div>
                                                {readNotes(active.filters).length > 0 && (
                                                    <p className="text-[11px] text-gray-400 mt-1.5">How we read it: {readNotes(active.filters).join(' · ')}</p>
                                                )}
                                                {active.tip && (
                                                    <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2">
                                                        💡 {active.tip}
                                                    </div>
                                                )}
                                                {products.length === 0 && !isUngatedMode && (
                                                    <p className="text-sm text-gray-500 mt-3">Try refining: a wider price range, more sellers, or fewer filters.</p>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Ungated mode: scan progress + "Check more products" */}
                                    {isRoundMode && !active.pending && (
                                        <div className="bg-white rounded-xl shadow p-4">
                                            {scan?.running ? (
                                                <div>
                                                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                                                        <span className="flex items-center gap-2 text-gray-700 font-semibold">
                                                            <Loader2 size={16} className="animate-spin text-teal-600" />
                                                            {scan.roundTotal > 0
                                                                ? <>{scanLabel} {scan.roundChecked.toLocaleString()} / {scan.roundTotal.toLocaleString()} products · <span className="text-green-700">{scan.roundAccepted} {acceptedLabel}</span></>
                                                                : 'Pulling more products to check…'}
                                                        </span>
                                                        {scan.roundTotal > 0 && (
                                                            <button onClick={stopScan} className="px-3 py-1 text-xs font-semibold border border-gray-300 rounded-lg hover:bg-gray-50">Stop</button>
                                                        )}
                                                    </div>
                                                    {scan.roundTotal > 0 && (
                                                        <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                            <div className="h-full bg-gradient-to-r from-teal-500 to-blue-600 transition-all" style={{ width: `${Math.round((scan.roundChecked / scan.roundTotal) * 100)}%` }} />
                                                        </div>
                                                    )}
                                                    <p className="text-[11px] text-gray-400 mt-1.5">
                                                        {active.mode === 'influencer'
                                                            ? (scan.fullLeft > 0 ? `Loading full data for ${scan.fullLeft} more…` : 'Products appear as they load.')
                                                            : 'Product details are only pulled for products you can sell.'} You can keep browsing while this runs.
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="flex flex-wrap items-center justify-between gap-3">
                                                    <div className="text-sm text-gray-700">
                                                        {scan
                                                            ? (active.mode === 'influencer'
                                                                ? (infFiltering
                                                                    ? <>We checked <strong>{scan.checked.toLocaleString()}</strong> products — <strong className="text-green-700">{scan.accepted.toLocaleString()}</strong> {matchWord}.</>
                                                                    : <>Loaded <strong>{scan.accepted.toLocaleString()}</strong> products.</>)
                                                                : <>We checked <strong>{scan.checked.toLocaleString()}</strong> products — <strong className="text-green-700">{scan.accepted.toLocaleString()}</strong> were {acceptedWord} for you.</>)
                                                            : <>Showing the products saved for this search.</>}
                                                        {!(active.has_more || remainderRef.current.length) && (
                                                            <div className="text-xs text-gray-500 mt-1">That's everything for this question. Try refining: a wider price range, another category, or fewer filters.</div>
                                                        )}
                                                        {(active.has_more || remainderRef.current.length > 0) && (scan?.accepted || 0) < 20 && (
                                                            (active.mode !== 'influencer' || infFiltering) && <div className="text-xs text-gray-500 mt-1">Not many {acceptedLabel} matches. {moreLabel}, or refine your question to widen the search.</div>
                                                        )}
                                                    </div>
                                                    {(active.has_more || remainderRef.current.length > 0) && (
                                                        <button
                                                            onClick={checkMore}
                                                            disabled={outOfSearches}
                                                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition shadow-lg text-sm disabled:opacity-50"
                                                        >
                                                            {active.mode === 'influencer' && !infFiltering ? <ChevronDown size={16} /> : <Shield size={16} />} {moreLabel} <span className="text-[10px] font-normal opacity-80">(1 search)</span>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Influencer Pro results */}
                                    {isInfluencer && products.length > 0 && (
                                        <InfluencerResults key={active.id} products={products} amazonDomain={amazonDomain} currency={currency} fileName={active.question || 'mip_gpt'} filters={active.filters} onRetry={retryCard} />
                                    )}

                                    {/* Filter bar (Seller Pro) */}
                                    {!isInfluencer && !isWalmartMode && products.length > 0 && (
                                        <div className="bg-white rounded-xl shadow p-3 space-y-2">
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                {[
                                                    ['all', `All (${counts.all})`, 'bg-gray-800 text-white border-gray-800'],
                                                    ['ungated', `✓ Ungated (${counts.ungated})`, 'bg-green-600 text-white border-green-600'],
                                                    ['ok', `✓ Ungated + ⚠ Approval (${counts.ok})`, 'bg-teal-600 text-white border-teal-600'],
                                                    ['approval', `⚠ Approval (${counts.approval})`, 'bg-orange-500 text-white border-orange-500'],
                                                    ['gated', `✗ Gated (${counts.gated})`, 'bg-red-600 text-white border-red-600'],
                                                ].map(([key, label, activeCls]) => (
                                                    <button
                                                        key={key}
                                                        onClick={() => setV({ gated: key })}
                                                        className={`px-3 py-1 rounded-full text-xs font-bold border transition ${view.gated === key ? activeCls : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                                {amazonLinked && counts.checking > 0 && !needsLogin && (
                                                    <span className="flex items-center gap-1 text-xs text-blue-600 font-semibold ml-1">
                                                        <Loader2 size={12} className="animate-spin" /> Checking gating… {counts.checking} left
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                                                <button onClick={() => setShowSellerFilters(x => !x)}
                                                    className={`flex items-center gap-1 px-3 py-1 rounded-md font-bold border transition ${sellerActiveRanges.length ? 'bg-teal-50 text-teal-700 border-teal-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                                                    <SlidersHorizontal size={13} /> Filters{sellerActiveRanges.length ? ` (${sellerActiveRanges.length})` : ''}
                                                </button>
                                                <select value={view.sort} onChange={e => setV({ sort: e.target.value })} className="px-2 py-1 border border-gray-200 rounded-md text-xs">
                                                    {SELLER_SORTS.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
                                                </select>
                                                <input type="text" value={view.brand} onChange={e => setV({ brand: e.target.value })} placeholder="Brand…" className="w-28 px-2 py-1 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-teal-400" />
                                                {categories.length > 1 && (
                                                    <select value={view.category} onChange={e => setV({ category: e.target.value })} className="px-2 py-1 border border-gray-200 rounded-md text-xs">
                                                        <option value="">All categories</option>
                                                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                )}
                                                <span className="ml-auto text-gray-400">Showing {visible.length} of {products.length}</span>
                                            </div>
                                            {sellerActiveRanges.length > 0 && (
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    {sellerActiveRanges.map(({ m, text }) => (
                                                        <span key={m.key} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-teal-50 text-teal-800 border border-teal-200 rounded-full pl-2 pr-1 py-0.5">
                                                            {text}
                                                            <button onClick={() => clearRange(m.key)} className="w-4 h-4 rounded-full hover:bg-teal-200 flex items-center justify-center" title="Remove"><X size={10} /></button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            {showSellerFilters && (
                                                <div className="border-t border-gray-100 pt-2">
                                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1.5">
                                                        {SELLER_METRICS.map(m => (
                                                            <div key={m.key} className="flex items-center gap-1.5 text-xs text-gray-600">
                                                                <span className="w-28 flex-shrink-0 truncate" title={m.label}>{m.label}</span>
                                                                <input type="number" placeholder="min" value={view.ranges[m.key]?.min ?? ''} onChange={e => setRange(m.key, 'min', e.target.value)} className={inputCls} />
                                                                <span className="text-gray-300">–</span>
                                                                <input type="number" placeholder="max" value={view.ranges[m.key]?.max ?? ''} onChange={e => setRange(m.key, 'max', e.target.value)} className={inputCls} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
                                                        {checkFilter("Hide Amazon-selling", "hideAmazon")}
                                                        {checkFilter("Hide variations", "hideVariations")}
                                                        {checkFilter("Price below 90d avg", "belowAvg")}
                                                        {checkFilter("Has monthly sold", "hasSold")}
                                                        {checkFilter("Has UPC", "hasUpc")}
                                                        <button onClick={() => setView(v => ({ ...EMPTY_VIEW, gated: v.gated }))} className="text-gray-400 hover:text-gray-700 font-semibold">Clear all</button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {isInfluencer ? null : isWalmartMode ? products.length > 0 && (
                                        <div className="space-y-3">
                                            <div className="bg-white rounded-xl shadow p-3 text-sm text-gray-700 flex flex-wrap items-center gap-2">
                                                <WmLogo className="w-4 h-4" />
                                                <span><strong className="text-blue-700">{wmStats.found}</strong> of {products.length} products found on Walmart (UPC match)</span>
                                                {wmStats.pending > 0 && (
                                                    <span className="flex items-center gap-1 text-blue-600 font-semibold">
                                                        <Loader2 size={13} className="animate-spin" /> Checking {wmStats.pending} more…
                                                    </span>
                                                )}
                                            </div>
                                            {(() => {
                                                let n = 0;
                                                return products.map(p => {
                                                    const s = wmMap[p.asin];
                                                    if (s?.status !== 'found') return null;
                                                    return (
                                                        <WalmartRow
                                                            key={p.asin}
                                                            p={p}
                                                            w={s.item}
                                                            index={n++}
                                                            amazonDomain={amazonDomain}
                                                            currency={currency}
                                                            keepaDomain={active.domain || 1}
                                                            gated={amazonLinked ? gatedMap[p.asin] : 'needs_login'}
                                                            onRecheck={recheckOne}
                                                            onAmazonOpen={(asin) => { if (amazonLinked && !gatedMap[asin]) checkGating([asin], gatingRunRef.current); }}
                                                        />
                                                    );
                                                });
                                            })()}
                                            {products.filter(p => wmMap[p.asin]?.status === 'loading').map(p => <WmSkeleton key={p.asin} />)}
                                            {wmStats.none > 0 && (
                                                <div className="pt-2 text-xs font-bold text-gray-500 uppercase tracking-wide">
                                                    Not found on Walmart by UPC ({wmStats.none}) · you can still search them by title
                                                </div>
                                            )}
                                            {products.filter(p => wmMap[p.asin]?.status === 'none').map((p, i) => (
                                                <NoMatchRow key={p.asin} p={p} index={i} amazonDomain={amazonDomain} currency={currency} />
                                            ))}
                                        </div>
                                    ) : visible.length > 0 ? (
                                        <div className="space-y-3">
                                            {visible.slice(resultsPage * PAGE_SIZE, (resultsPage + 1) * PAGE_SIZE).map((p, i) => (
                                                <ResultCard
                                                    key={p.asin}
                                                    p={p}
                                                    index={resultsPage * PAGE_SIZE + i}
                                                    amazonDomain={amazonDomain}
                                                    currency={currency}
                                                    keepaDomain={active.domain || 1}
                                                    gated={amazonLinked ? gatedMap[p.asin] : 'needs_login'}
                                                    onRecheck={recheckOne}
                                                />
                                            ))}
                                            <Pager page={resultsPage} total={visible.length} onPage={setResultsPage} />
                                        </div>
                                    ) : products.length > 0 && (
                                        <div className="text-center text-sm text-gray-500 py-8 bg-white rounded-xl shadow">
                                            {view.gated !== 'all' && counts.checking > 0
                                                ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Checking your gated status… matching products will appear here.</span>
                                                : 'No products match these filters. Try clearing some, or get more below.'}
                                        </div>
                                    )}

                                    {!isRoundMode && active.has_more && products.length > 0 && (
                                        <div className="flex justify-center pt-2 pb-6">
                                            <button
                                                onClick={loadMore}
                                                disabled={loadingMore || outOfSearches}
                                                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-60 shadow-lg"
                                            >
                                                {loadingMore ? <RefreshCw className="animate-spin" size={18} /> : <ChevronDown size={18} />}
                                                {loadingMore ? 'Loading…' : <>Load 100 more <span className="text-[10px] font-normal opacity-80">(1 search)</span></>}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </main>
                    </div>
                )}

                {/* How to reverse source the exported CSV */}
                {showRsHelp && (
                    <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowRsHelp(false)}>
                        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => setShowRsHelp(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"><X size={16} /></button>
                            <h3 className="text-lg font-bold text-gray-800">Find suppliers for these products</h3>
                            <ol className="mt-3 space-y-2 text-sm text-gray-700 list-decimal list-inside">
                                <li><strong>Export to CSV</strong> — the file downloads to your computer.</li>
                                <li>Go to the <strong>Reverse Scan</strong> page.</li>
                                <li><strong>Upload the CSV file.</strong> Amazon searches use the <strong>ASIN</strong> column, Walmart searches use the <strong>Walmart Item ID</strong> column.</li>
                            </ol>
                            <div className="mt-5 flex gap-2">
                                <button onClick={exportCsv} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                                    <Download size={14} /> Download again
                                </button>
                                <a href="/reverse-scan" target="_blank" rel="noopener noreferrer" className="flex-1 text-center px-3 py-2 rounded-lg bg-gradient-to-r from-teal-500 to-blue-600 text-white text-sm font-bold hover:opacity-90">
                                    Go to Reverse Scan →
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MspGptPage;